import React, { useCallback, useEffect, useState } from "react";
import ShippingAddressSection, {
  EMPTY_ADDRESS,
  hasErrors,
  regionCodeFromMastersList,
  useShippingForm,
  validateAddress,
} from "./ShippingAddressSection";

function ShippingSenderStep({ values, setData, navigation, t }) {
  const locale = values.locale || "en";

  const [mastersEmirateList, setMastersEmirateList] = useState([]);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(
      process.env.REACT_APP_MASTERS_API_BASE_URL +
        "/locations/api/Emirates?IncludeCityAsEmirate=true"
    )
      .then((r) => r.json())
      .then((data) => setMastersEmirateList(data.data?.list || []))
      .catch(() => {});
  }, []);

  const fetchAreas = useCallback((emirateId, setter) => {
    fetch(
      process.env.REACT_APP_MASTERS_API_BASE_URL +
        "/locations/api/Regions?EmirateId=" +
        emirateId
    )
      .then((r) => r.json())
      .then((data) => setter(data.data?.list || []))
      .catch(() => {});
  }, []);

  const savedSender = values.shippingDetails?.sender || null;
  const appointmentReference =
    (values.goodConductAppointmentReference &&
      String(values.goodConductAppointmentReference).trim()) ||
    "";

  const senderInitialData = {
    ...EMPTY_ADDRESS,
    ...(!savedSender ? {
      firstName: values.firstName || "",
      lastName: values.lastName || "",
      email: values.emailAddress || "",
      mobile: values.mobileNumber || "",
      mobileDialCode: values.mobileDialCode || "",
      isMobileValid: Boolean(values.mobileNumber),
    } : {}),
    ...(savedSender || {}),
    country: "United Arab Emirates",
    countryCode: "AE",
  };

  const {
    formData,
    errors,
    setErrors,
    areaLookup,
    handleChange,
    handlePhoneChange,
    handleFlagChange,
    handleCityChange,
    touchAll,
  } = useShippingForm({
    initialData: senderInitialData,
    cityLookup: [],
    fetchAreas,
    mastersEmirateList,
    syncAreaToMastersRegionCode: true,
    validateOptions: { requireCoords: false },
  });

  const handleProceed = () => {
    setSubmitted(true);
    touchAll();
    const errs = validateAddress(formData, { requireCoords: false });
    setErrors(errs);
    if (hasErrors(errs)) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const areaRegionCode =
      regionCodeFromMastersList(areaLookup, formData.area) ??
      (areaLookup.some((a) => String(a.code) === String(formData.area))
        ? String(formData.area)
        : formData.area);
    setData({
      name: "shippingDetails",
      value: {
        ...(values.shippingDetails || {}),
        sender: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          mobile: `+${formData.mobileDialCode} ${formData.mobile}`,
          landline: formData.landline,
          country: "United Arab Emirates",
          countryCode: "AE",
          city: formData.cityName,
          cityName: formData.cityName,
          cityCode: formData.cityCode,
          area: areaRegionCode,
          street: formData.street,
          building: formData.building,
          apartmentNo: formData.apartmentNo,
          floor: formData.floor,
          zip: formData.zip,
          additionalAddress: formData.additionalAddress,
          coords: null,
        },
      },
    });
    navigation.go("shipping-receiver");
  };

  return (
    <div className="shipping-sender-step mt-4 mb-5">
      <div className="row align-items-center mb-4 gc-page-header">
        <div className="col-auto">
          <img
            src="./images/user-icon.svg"
            alt=""
            className="gc-section-icon"
          />
        </div>
        <div className="col">
          <h3 className="mb-0 gc-page-title">{t("SHIPPING_DETAILS_TITLE")}</h3>
          <p className="text-muted small mb-0 mt-1">
            {t("SHIPPING_DETAILS_SUBTITLE")}
          </p>
        </div>
      </div>

      {appointmentReference ? (
        <div className="alert alert-info mb-4" role="status">
          <p className="mb-0" dir="ltr">
            {t("GC_SUCCESS_REFERENCE", { reference: appointmentReference })}
          </p>
        </div>
      ) : null}

      {submitted && hasErrors(errors) && (
        <div className="alert alert-danger mb-4" role="alert">
          {t("ENTER_REQUIRED_FIELDS")}
        </div>
      )}

      <div className="gc-section-card mb-4">
        <div className="gc-section-heading">
          <img
            src="./images/user-icon.svg"
            alt=""
            className="gc-inline-icon"
          />
          <span>{t("SHIPPING_SENDER_TITLE")}</span>
        </div>

        <ShippingAddressSection
          formData={formData}
          errors={errors}
          cityLookup={mastersEmirateList}
          areaLookup={areaLookup}
          onChange={handleChange}
          onPhoneChange={handlePhoneChange}
          onFlagChange={handleFlagChange}
          onCityChange={(e) => handleCityChange(e, mastersEmirateList)}
          hasCompanyName={false}
          t={t}
          locale={locale}
          sectionKey="sender"
          showMap={false}
        />
      </div>

      <div className="row mt-5">
        <div className="col-6 dir-left">
          <button
            type="button"
            className="btn btn-lg btn-default btn-back"
            onClick={() => navigation.go("good-enter-details")}
          >
            <img src="./images/arrow-left.svg" alt="" />
            {t("BACK")}
          </button>
        </div>
        <div className="col-6 dir-right">
          <button
            type="button"
            className="btn btn-primary btn-lg emx-button"
            onClick={handleProceed}
          >
            {t("PROCEED")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ShippingSenderStep;
